/**
 * What Luma is saying, over her head (2026-10-06): one host now speaks to the whole room, so
 * everyone can read her line as well as hear it, whoever asked. One canvas sprite, redrawn
 * only when the line changes.
 */
import * as THREE from 'three';
import { PALETTE, font, roundRect, wrapText } from '../dashboards/charts.ts';

const W = 900;
const H = 300;
const MAX_LINES = 5;

export class HostCaption {
  readonly sprite: THREE.Sprite;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private key = '';

  constructor() {
    this.canvas.width = W;
    this.canvas.height = H;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: this.texture, transparent: true, toneMapped: false }),
    );
    this.sprite.scale.set(1.05, 0.35, 1);
    this.sprite.center.set(0.5, 0);
    this.sprite.renderOrder = 190;
    this.sprite.visible = false;
    this.sprite.name = 'host-caption';
  }

  /** Shows a line: who it is for, then the words (the latest words if it runs long). */
  show(header: string, body: string): void {
    const key = `${header}\n${body}`;
    if (key !== this.key) {
      this.key = key;
      this.draw(header, body);
    }
    this.sprite.visible = true;
  }

  hide(): void {
    this.sprite.visible = false;
  }

  get visible(): boolean {
    return this.sprite.visible;
  }

  private draw(header: string, body: string): void {
    const ctx = this.canvas.getContext('2d')!;
    ctx.clearRect(0, 0, W, H);
    ctx.font = font(26, 500);
    let lines = wrapText(ctx, body, W - 60);
    if (lines.length > MAX_LINES) lines = [...lines.slice(0, MAX_LINES - 1), '…'];
    const h = 64 + lines.length * 40;
    const y0 = H - h;
    ctx.fillStyle = PALETTE.overlay;
    roundRect(ctx, 4, y0, W - 8, h - 4, 22);
    ctx.fill();
    ctx.strokeStyle = PALETTE.accentText;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.textBaseline = 'top';
    ctx.font = font(22, 700);
    ctx.fillStyle = PALETTE.accentText;
    ctx.fillText(header, 30, y0 + 16);
    ctx.font = font(26, 500);
    ctx.fillStyle = PALETTE.text;
    lines.forEach((l, i) => ctx.fillText(l, 30, y0 + 52 + i * 40));
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
    (this.sprite.material as THREE.Material).dispose();
  }
}
